import React, { useRef, useState, useEffect, useCallback } from "react";
import style from "css/hotdesking.module.css";
import { useSelector } from "react-redux";
import pinIcon from "asset/hotdesking/pin.svg";
import BookResource from "./BookResource";
import { Dialog } from "antd-mobile";
import axios from "axios";
import HotdeskingConfig from "../Config";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const resourceRadius = 15;
const tailHeight = 10;
const initailScale = 1;
const type = {
  AVAILABLE: {
    value: "AVAILABLE",
    label: "예약 중",
    color: "#f16569",
    hover: "#eb1c22",
    blur: "#f16569cc",
  },
  IN_USE: {
    value: "IN_USE",
    label: "사용 중",
    color: "#fea85e",
    hover: "#fa7807",
    blur: "#fea85ecc",
  },
  NOT_USE: {
    value: "NOT_USE",
    label: "예약 가능",
    color: "#5ee2c3",
    hover: "#00ebb4",
    blur: "#5ee2c3cc",
  },
  NOT_AVAILABLE: {
    value: "NOT_AVAILABLE",
    label: "사용 불가능",
    color: "#454545",
    hover: "#454545",
    blur: "#454545cc",
  },
};

const PlanCanvas = ({
  bookList,
  resourceList,
  location,
  isFind,
  setIsFind,
  isRefresh,
  setIsRefresh,
  onShowBook,
  onRefreshBook,
  onError,
  date,
  time,
  serverDate,
  setTime,
  resource,
}) => {
  const { t } = useTranslation();
  //system redux
  const systemPayload = useSelector((state) => state.system);
  const userPayload = useSelector((state) => state.user);
  //ref
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(initailScale);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMove, setIsMove] = useState(false);
  const lastPosition = useRef({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const imageSize = useRef({ width: 1, height: 1 });
  const [init, setInit] = useState(true);
  const [pin, setPin] = useState(null);
  const [isShow, setIsShow] = useState(false);
  const [balloonPosition, setBalloonPosition] = useState({ x: 0, y: 0 });

  const moveToResource = useCallback(() => {
    if (resource === null) {
      return;
    }

    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (!canvas || !img) return;

    // 이미지 중심 좌표 기준에서 리소스 위치 계산
    const imgCenterX = img.width / 2;
    const imgCenterY = img.height / 2;
    const targetX = imgCenterX + resource.coordinateX;
    const targetY = imgCenterY + resource.coordinateY;

    // 캔버스의 중앙이 해당 리소스를 바라보도록 offset 계산
    const canvasCenterX = canvas.width / 2;
    const canvasCenterY = canvas.height / 2;

    const dx = (canvas.width / scale - img.width) / 2;
    const dy = (canvas.height / scale - img.height) / 2;

    const targetOffsetX = canvasCenterX - (targetX + dx) * scale;
    const targetOffsetY = canvasCenterY - (targetY + dy) * scale;

    setOffset({ x: targetOffsetX, y: targetOffsetY });

    setBalloonPosition({
      x: canvasCenterX,
      y: canvasCenterY - resourceRadius * scale - tailHeight * scale,
    });
    setIsShow(true);
  }, [resource, scale]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    ctx.scale(scale, scale);
    ctx.translate(offset.x / scale, offset.y / scale);

    if (imageRef.current) {
      const img = imageRef.current;
      imageSize.current = { width: img.width, height: img.height };

      const dx = (canvas.width / scale - img.width) / 2;
      const dy = (canvas.height / scale - img.height) / 2;
      ctx.translate(dx, dy);

      ctx.drawImage(img, 0, 0, img.width, img.height);

      resourceList.forEach((resource) => {
        const imgCenterX = img.width / 2;
        const imgCenterY = img.height / 2;
        const adjustedX = imgCenterX + resource.coordinateX;
        const adjustedY = imgCenterY + resource.coordinateY;
        ctx.beginPath();
        ctx.arc(adjustedX, adjustedY, resourceRadius, 0, Math.PI * 2);

        let color = type.NOT_USE.color;
        let isBook = false;

        bookList.forEach((book) => {
          if (book.resource.id === resource.id) {
            switch (book.status) {
              case type.AVAILABLE.value:
                if (userPayload.id === book.member.id) isBook = true;
                color = type.AVAILABLE.color;
                break;
              case type.IN_USE.value:
                if (userPayload.id === book.member.id) isBook = true;
                color = type.IN_USE.color;
                break;
              case type.NOT_AVAILABLE.value:
                color = type.NOT_AVAILABLE.color;
                break;
              default:
                return;
            }
          }
        });
        ctx.fillStyle = color;
        ctx.fill();

        if (isBook) {
          const iconSize = resourceRadius * scale * 1.2;
          ctx.drawImage(
            pin,
            adjustedX - iconSize / 2,
            adjustedY - iconSize / 2,
            iconSize,
            iconSize
          );
        }
      });
    }

    ctx.restore();

    if (resource !== null && isFind) {
      setIsMove(true);
    }
  }, [
    bookList,
    isFind,
    offset.x,
    offset.y,
    pin,
    resource,
    resourceList,
    scale,
    userPayload.id,
  ]);

  const updateCanvas = useCallback(() => {
    if (!imageRef.current) {
      if (!location) {
        return;
      }
      const img = new Image();
      // img.src = process.env.REACT_APP_FILE_URL + location.source;
      img.src = location.source;
      img.onload = () => {
        imageRef.current = img;
        imageSize.current = { width: img.width, height: img.height };
        drawCanvas();
      };
    }
  }, [drawCanvas, location]);

  const removeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();

    imageRef.current = null;
    imageSize.current = { width: 1, height: 1 };
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = pinIcon;
    img.onload = () => setPin(img);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = canvas.parentElement.clientHeight;
    }
  }, []);

  useEffect(() => {
    if (init && location != null) {
      updateCanvas();
      setInit(false);
      window.addEventListener("resize", updateCanvas);
      return () => {
        window.removeEventListener("resize", updateCanvas);
      };
    }
  }, [init, location, updateCanvas]);

  useEffect(() => {
    if (isRefresh) {
      if (location === null) {
        removeCanvas();
      } else {
        updateCanvas();
      }
      setIsRefresh(false);
    }
  }, [
    isRefresh,
    location,
    removeCanvas,
    setIsRefresh,
    updateCanvas,
    drawCanvas,
  ]);

  useEffect(() => {
    if (imageRef.current && !init && !isRefresh) {
      drawCanvas();
    }
  }, [drawCanvas, init, isRefresh]);

  useEffect(() => {
    if (isFind && resource !== null) {
      updateCanvas();
      setIsFind(false);
    }
  }, [isFind, moveToResource, resource, setIsFind, updateCanvas]);

  useEffect(() => {
    if (isMove && !isFind && imageRef) {
      moveToResource();
      setIsMove(false);
    }
  }, [isFind, isMove, moveToResource]);

  const formatStartTime = () => {
    if (date === serverDate) {
      const nowTime = dayjs(new Date()).format("HH:mm:ss");

      if (time.start > nowTime) {
        return dayjs(new Date(date + " " + time.start)).format(
          "YYYY-MM-DD HH:mm:ss"
        );
      }

      setTime({
        ...time,
        start: nowTime,
      });
      return dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
    }
    return dayjs(new Date(date + " " + time.start)).format(
      "YYYY-MM-DD HH:mm:ss"
    );
  };

  const onBook = (resource) => {
    const data = {
      resource_type: resource.type,
      resource_id: resource.id,
      start_dttm: formatStartTime(),
      end_dttm: dayjs(new Date(date + " " + time.end)).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
    };

    if (resource.endpoint !== undefined) {
      const endpoint = {
        endpoint_id: resource.endpoint.id,
        endpoint_name: resource.endpoint.name,
        endpoint_type: resource.endpoint.type,
      };
      data.endpoint = endpoint;
    }

    axios
      .post(`${HotdeskingConfig.baseURI}/book`, data)
      .then(() => {
        onRefreshBook();
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MODAL.BOOKING.MESSAGE.ERROR"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        // setLoading(false);
      });
  };
  const onOpenBook = (resource) => {
    if (!systemPayload.isOnline) {
      Dialog.show({
        className: `${style.dialog} ${style.confirm}`,
        bodyClassName: `${style.dialog_body}`,
        bodyStyle: { padding: 0 },
        content: <BookResource onSubmit={() => onBook(resource)} />,
      });
      return;
    }
    if (resource.type === "CD1401" && resource.endpoint !== undefined) {
      // //화상회의 장비가 있는 회의실
      // setIsBookEndpoint(true);
    } else {
      Dialog.show({
        className: `${style.dialog} ${style.confirm}`,
        bodyClassName: `${style.dialog_body}`,
        bodyStyle: { padding: 0 },
        content: <BookResource onSubmit={() => onBook(resource)} />,
      });
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    setScale((prev) => Math.min(Math.max(prev + e.deltaY * -0.01, 1), 5));
  };

  const handlePointerDown = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    const canvasX = (clientX - rect.left - offset.x) / scale;
    const canvasY = (clientY - rect.top - offset.y) / scale;

    if (!imageRef.current) return;
    const img = imageRef.current;
    const dx = (canvas.width / scale - img.width) / 2;
    const dy = (canvas.height / scale - img.height) / 2;

    const imageTouchX = canvasX - dx;
    const imageTouchY = canvasY - dy;

    let isResourceTouched = false;
    resourceList.forEach((resource) => {
      const imgCenterX = img.width / 2;
      const imgCenterY = img.height / 2;
      const resourceX = imgCenterX + resource.coordinateX;
      const resourceY = imgCenterY + resource.coordinateY;
      const distance = Math.sqrt(
        Math.pow(imageTouchX - resourceX, 2) +
          Math.pow(imageTouchY - resourceY, 2)
      );

      if (distance < resourceRadius) {
        let isBookAvailable = true;

        bookList.forEach((book) => {
          if (book.resource.id === resource.id) {
            e.stopPropagation();
            switch (book.status) {
              case "AVAILABLE":
                onShowBook(book, resource);
                isBookAvailable = false;
                break;
              case "IN_USE":
                onShowBook(book, resource);
                isBookAvailable = false;
                break;
              case "NOT_AVAILABLE":
                isBookAvailable = false;
                break;
              default:
                break;
            }
          }
        });
        if (isBookAvailable) {
          onOpenBook(resource);
        }
        isResourceTouched = true;
      }
    });

    if (!isResourceTouched) {
      setIsDragging(true);
      lastPosition.current = { x: clientX, y: clientY };
    }
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    const deltaX = clientX - lastPosition.current.x;
    const deltaY = clientY - lastPosition.current.y;
    setOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
    lastPosition.current = { x: clientX, y: clientY };
    drawCanvas();
  };

  const handlePointerUp = (e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    setIsDragging(false);
  };

  useEffect(() => {
    const disableScroll = (e) => e.preventDefault();
    document.addEventListener("touchmove", disableScroll, { passive: true });
    return () => document.removeEventListener("touchmove", disableScroll);
  }, []);

  return (
    <section className={style.canvas_wrap}>
      <section className={style.canvas_container}>
        {/* <section className={style.canvas_btn}>
        <button onClick={() => setScale((s) => Math.min(s + 0.5, 5))}>+</button>
        <button onClick={() => setScale((s) => Math.max(s - 0.5, 1))}>-</button>
          </section> */}

        {isShow && imageRef.current && (
          <Balloon
            position={balloonPosition}
            resource={resource}
            setIsShow={setIsShow}
          />
        )}

        <canvas
          ref={canvasRef}
          onWheel={handleWheel}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        ></canvas>
      </section>
    </section>
  );
};

export default PlanCanvas;

const Balloon = ({ position, resource, setIsShow }) => {
  const onRemove = useCallback(() => {
    setIsShow(false);
  }, [setIsShow]);

  useEffect(() => {
    window.addEventListener("touchstart", onRemove);
    return () => window.removeEventListener("touchstart", onRemove);
  }, [onRemove]);

  return (
    <div
      className={style.balloon}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {resource?.title}
      <div className={style.balloon_tail} />
    </div>
  );
};
